import { SavedSearch, TabType, Filter } from './types';
import { CONTEXT_PARAMS, OPERATOR_MAP, isValidFilterValue } from './favoritesUtils';
import CompleteBadge from './CompleteBadge';

interface SearchDetailsProps {
  search: SavedSearch;
  activeTab: TabType;
}

const SearchDetails = ({ search, activeTab }: SearchDetailsProps) => {
  const getSearchDetails = (filters: Filter[]) => {
    if (!Array.isArray(filters) || filters.length === 0) {
      return 'No column filters applied';
    }

    const columnFilters = filters.filter((f: Filter) => !CONTEXT_PARAMS.includes(f.name));
    if (columnFilters.length === 0) {
      return 'No column filters applied';
    }

    const filtersStr = columnFilters
      .filter((f: Filter) => isValidFilterValue(f.value))
      .map((f: Filter) => {
        const label = f.label || f.name || '';
        const value = Array.isArray(f.value) ? f.value.join(', ') : f.value;
        const operator = f.operator ? ` ${OPERATOR_MAP[f.operator] || f.operator} ` : ': ';
        return `${label}${operator}${value}`;
      })
      .join(', ');

    return filtersStr || 'No column filters applied';
  };

  return (
    <div style={{ marginTop: '8px' }}>
      {activeTab === 'searches' && search.type === 'enrichment' && (
        <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
          <strong>Enrichment:</strong>{' '}
          {search.searchContext?.searchType === 'VARIANTS' ? 'Variants' : 'Genes'}
          {search.searchContext?.analysisMethod ? ` · ${search.searchContext.analysisMethod}` : ''}
          {search.searchContext?.itemCount != null ? ` · ${search.searchContext.itemCount} identifiers` : ''}
          {search.searchContext?.source ? ` · source ${search.searchContext.source}` : ''}
          {(search.searchContext?.enrichmentResults?.length || search.searchContext?.gseaResults?.length) && (
            <>
              <br />
              <strong>Saved results:</strong>{' '}
              {[
                search.searchContext?.enrichmentResults?.length
                  ? `${search.searchContext.enrichmentResults.length} ORA`
                  : null,
                search.searchContext?.gseaResults?.length
                  ? `${search.searchContext.gseaResults.length} DSEA`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </>
          )}
        </div>
      )}

      {activeTab === 'searches' && search.type === 'complete' && (
        <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
          <CompleteBadge />
          <br />
          <strong>Includes:</strong> Filters + Columns + Order
          {search.searchContext && (
            <>
              <br />
              <strong>Context:</strong> {search.searchContext.view} - {search.searchContext.tab}
            </>
          )}
        </div>
      )}

      {activeTab === 'searches' && search.type === 'filter' && (
        <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
          <strong>Column filters:</strong> {getSearchDetails(search.filters)}
        </div>
      )}

      {activeTab === 'searches' &&
        search.type !== 'complete' &&
        search.type !== 'filter' &&
        search.type !== 'enrichment' && (
          <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
            <strong>Search:</strong> {search.searchQuery || 'N/A'}
          </div>
        )}

      {activeTab === 'columns' && search.type === 'complete' && (
        <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
          <CompleteBadge />
          <br />
          <strong>Includes:</strong> Filters + Columns + Order
          {search.searchContext && (
            <>
              <br />
              <strong>Context:</strong> {search.searchContext.view} - {search.searchContext.tab}
            </>
          )}
        </div>
      )}

      {activeTab === 'columns' && search.type === 'columns' && (
        <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
          <strong>Visible columns:</strong>{' '}
          {search.columnVisibilityModel && typeof search.columnVisibilityModel === 'object'
            ? Object.values(search.columnVisibilityModel).filter(v => v === true).length
            : 0}{' '}
          columns
          {search.columnOrder && (
            <>
              <br />
              <strong>Custom order:</strong> Yes ({search.columnOrder.length} columns)
            </>
          )}
          {search.searchContext && (
            <>
              <br />
              <strong>Context:</strong> {search.searchContext.view} - {search.searchContext.tab}
            </>
          )}
        </div>
      )}

      {activeTab === 'scores' && search.type === 'score' && (
        <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
          <strong>Source Weights:</strong>
          <br />
          {search.sourceWeights && search.sourceWeights.length > 0 ? (
            <div style={{ marginTop: '4px', marginLeft: '8px' }}>
              {search.sourceWeights.map((sw, idx) => (
                <div key={idx} style={{ marginBottom: '2px' }}>
                  • <strong>{sw.name.replace('_', ' ')}</strong>: {sw.score.toFixed(2)}
                </div>
              ))}
            </div>
          ) : (
            'No weights defined'
          )}
          {search.searchContext && (
            <>
              <br />
              <strong>Context:</strong> {search.searchContext.view} - {search.searchContext.tab}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchDetails;
